import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch, AnyAction } from 'redux';
import { TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import { Messages } from '../../../gen-src/Localize';
import { AppState } from '../../app/state/AppState';
import * as Layout from '../../common/styles/Layout';
import { classNames } from '../../common/styles/Utils';
import { changeContentType } from '../actions/ChangeContentTypeAction';
import { ContentDefinitions } from '../models/contentDefinitions';
import { ContentType } from '../models/ContentType';
import { DefaultContentTypeOrder } from '../models/ContentTypeOrder';
import { PageType } from '../models/PageType';
import { MixedContentTypeSearchResult } from '../models/SearchResultTypes';
import { NavigationService } from '../services/NavigationService';
import {
  MixedContentHeaderStyle,
  MixedContentLinkStyle,
  MixedContentSectionStyle,
  MixedContentTitleStyle,
  MixedContentTypeResultStyles,
} from '../styles/SearchResultPageStyles';
import { ConnectedResultListWrapper } from './ResultListWrapper';
import { SortSelection } from '../models/SortSelection';
import { TelemetryService } from '../services/TelemetryService';
import { formatTotalSearchResultCount } from '../../common/utils/L10n';
import { FeatureFlag, FeatureFlagService } from '../services/FeatureFlagService';

export const MixedContentTypeResultID = 'MixedContentTypeResult';
// eslint-disable-next-line prettier/prettier
export interface MixedContentTypeResultPageOwnProps { }

export type MixedContentTypeResultPageProps = MixedContentTypeResultPageOwnProps &
  MixedContentTypeResultPageStateProps &
  MixedContentTypeResultPageDispatchProps;

export const MixedContentSectionTestID = 'MixedContentSection';
export const MixedContentTitleTestID = 'MixedContentTitle';

export function MixedContentTypeResultPage(props: MixedContentTypeResultPageProps): JSX.Element {
  const onSeeAllSelected = (contentType: ContentType) => {
    TelemetryService.instance.sendSearchResultClickedEvent({
      type: 'SearchResultClicked',
      eventSource: 'search-space',
      resultType: contentType.toString() + 's',
      seeAll: true,
      index: -1,
      total: -1,
      searchTerm: props.searchTerm,
      contentId: '-1',
    });
  };

  const contentTypeOrder =
    FeatureFlagService.instance.getValue(FeatureFlag.smarterSearchEnabled) && props.searchResult?.contentTypeOrder
      ? props.searchResult?.contentTypeOrder
      : DefaultContentTypeOrder;

  return (
    <div role="region" aria-label={Messages.Results()} className={MixedContentTypeResultStyles} data-tb-test-id={MixedContentTypeResultID}>
      {contentTypeOrder.map((contentType) => {
        const items = props.searchResult?.map[contentType]?.list || [];

        return props.searchResult?.map[contentType] && items.length > 0 ? (
          <div key={contentType} className={MixedContentSectionStyle} data-tb-test-id={MixedContentSectionTestID}>
            <div className={classNames(MixedContentHeaderStyle, Layout.flexH)} role="heading" aria-level={2}>
              <div className={classNames(MixedContentTitleStyle, Layout.flexAuto)} data-tb-test-id={MixedContentTitleTestID}>
                {ContentDefinitions[contentType].getSectionTitle()}
                <span>{formatTotalSearchResultCount(props.searchResult?.map[contentType].totalCount)}</span>
              </div>
              <div>
                <TextLinkWidget
                  kind={TextLinkKind.Standalone}
                  href={NavigationService.instance.getUrlByContentType(contentType, props.sortBy)}
                  testId="standalone"
                  className={MixedContentLinkStyle}
                  aria-label={Messages.SeeAll_aria_label() + ' ' + contentType + 's.'}
                  onClick={() => onSeeAllSelected(contentType)}
                >
                  {Messages.SeeAllLink()}
                </TextLinkWidget>
              </div>
            </div>
            <div>
              <ConnectedResultListWrapper
                contentType={contentType}
                pageType={PageType.mixed}
                items={items.slice(0, ContentDefinitions[contentType].maxItemCountInMixedContentTypeResultPage)}
              />
            </div>
          </div>
        ) : null;
      })}
    </div>
  );
}

export interface MixedContentTypeResultPageStateProps {
  searchResult?: MixedContentTypeSearchResult;
  sortBy?: SortSelection;
  searchTerm: string;
}

export interface MixedContentTypeResultPageDispatchProps {
  changeContentType: (contentType: ContentType) => void;
}

const mapStateToProps = (state: AppState) => ({
  searchResult: state.search.mixedContentType.searchResult,
  sorBy: state.search.sortBy,
  searchTerm: state.search.searchTerm,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      changeContentType,
    },
    dispatch
  );

export const ConnectedMixedContentTypeResultPage: React.ComponentClass<MixedContentTypeResultPageOwnProps> = connect(
  mapStateToProps,
  mapDispatchToProps
)(MixedContentTypeResultPage);
